import { GameState, Move } from '../game/types';
import type { PositionOccurrence } from '../game/repetition';
import { legalMoves } from '../game/rules';
import type { Bot } from './bot';
import { Level, LEVELS } from './levels';
import type { SearchOptions, SearchResult } from './search';
import { allocateThinkingTime } from './timeManager';

export type WorkerSearchOptions = Omit<SearchOptions, 'shouldAbort'>;

export type WorkerRequest = {
  type: 'search';
  requestId: string;
  state: GameState;
  options: WorkerSearchOptions;
};

export type WorkerResponse =
  | { type: 'result'; requestId: string; result: SearchResult }
  | { type: 'error'; requestId: string; message: string };

export interface WorkerLike {
  onmessage: ((event: MessageEvent<WorkerResponse>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  postMessage(message: WorkerRequest): void;
  terminate(): void;
}

export type WorkerFactory = () => WorkerLike;

let requestSequence = 0;

function nextRequestId(): string {
  requestSequence += 1;
  return `local-search-${Date.now()}-${requestSequence}`;
}

export class WorkerBackedBot implements Bot {
  private worker: WorkerLike | null = null;
  private pending: { requestId: string; resolve: (move: Move | null) => void } | null = null;

  constructor(
    readonly level: Level,
    private readonly workerFactory: WorkerFactory,
  ) {}

  chooseMove(state: GameState, history?: PositionOccurrence[]): Promise<Move | null> {
    this.cancel();

    const worker = this.workerFactory();
    const requestId = nextRequestId();
    const moves = legalMoves(state);
    const fallback = moves[0] ?? null;
    const config = LEVELS[this.level];
    const timeMs = allocateThinkingTime(state, moves, {
      minTimeMs: config.minTimeMs,
      maxTimeMs: config.timeMs,
      jitterRatio: config.jitterRatio,
    });
    this.worker = worker;

    return new Promise(resolve => {
      this.pending = { requestId, resolve };

      const finish = (move: Move | null): void => {
        if (this.worker !== worker || this.pending?.requestId !== requestId) return;
        this.pending = null;
        this.worker = null;
        worker.terminate();
        resolve(move);
      };

      worker.onmessage = event => {
        const response = event.data;
        if (response.requestId !== requestId) return;
        finish(response.type === 'result' ? response.result.move : fallback);
      };
      worker.onerror = () => finish(fallback);

      worker.postMessage({
        type: 'search',
        requestId,
        state,
        options: { ...config, timeMs, history },
      });
    });
  }

  cancel(): void {
    const pending = this.pending;
    this.pending = null;
    this.worker?.terminate();
    this.worker = null;
    pending?.resolve(null);
  }

  dispose(): void {
    this.cancel();
  }
}

export function createWorkerBot(level: Level): WorkerBackedBot {
  return new WorkerBackedBot(
    level,
    () => new Worker(new URL('./searchWorker.ts', import.meta.url), { type: 'module' }),
  );
}
